import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next"; // Assuming you're using react-i18next
import { MyAreaTitle } from "../components";
import { useQuery } from "react-query";
import { getUserGuide } from "../../../../../services/guide.service";
import { GuideDetails } from "../../../../../@types";
import { CircularImage } from "../../../../../components/circularImage";
import Button from "../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../components/button/types";
import { ReactComponent as RightArrow } from "../../../../../assets/rigth-arrow_active.svg";
import InfoIcon from "@mui/icons-material/Info";
function GuideHeader({ guideDetails }: { guideDetails: GuideDetails }) {
  const { t } = useTranslation("myArea");

  return (
    <div className="flex w-full bg-Yellow-light rounded-md flex-col md:flex-row items-center md:items-start">
      <div className="w-[160px] h-[160px] overflow-hidden p-2">
        <CircularImage src={`https:${guideDetails?.profilePicture}`} />
      </div>
      <div className="p-3">
        <h1 className="text-lg font-semibold">
          {`${guideDetails?.givenName} ${guideDetails?.surname}`.toUpperCase()}
        </h1>

        <div className="flex items-center my-2 text-Grey-dark">
          {t("guide.description")}
        </div>
        <a href="#about-me">
          <Button
            variant={ButtonVariant.SECONDARY}
            size={ButtonSize.SMALL}
            className="mr-5 px-3 mt-1"
          >
            <div className="flex items-center ">
              <span className="font-medium mr-3">
                {t("guide.more-about-me")}
              </span>
              <div className="">
                {" "}
                <RightArrow />
              </div>
            </div>
          </Button>
        </a>
      </div>
    </div>
  );
}

export function Guide() {
  const { t } = useTranslation("myArea");
  const [activeTab, setActiveTab] = useState<string>("schedule-meeting");
  const { data: guide, isLoading } = useQuery<GuideDetails>(
    ["guide"],
    getUserGuide
  );

  const tabs = useMemo(() => {
    const baseTabs = ["schedule-meeting", "all-meetings"];
    return baseTabs;
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1600);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1600);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (activeTab === "schedule-meeting" && !isLoading) {
      const script = document.createElement("script");
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [activeTab, isLoading]);

  if (!guide) {
    return (
      <div>
        <div className="hidden lg:flex">
          <MyAreaTitle title={t("guide.title")} />
        </div>
        <div>{t("guide.noGuide")}</div>
      </div>
    );
  }

  const formatMeetingDateTime = (meetingDate: Date) => {
    const dateObj = new Date(meetingDate);

    const currentLocale = "de-DE";

    const formattedDate = dateObj.toLocaleDateString(currentLocale, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "Europe/Berlin",
    });

    const formattedTime = dateObj.toLocaleTimeString(currentLocale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone: "Europe/Berlin",
    });
    return { formattedDate, formattedTime };
  };

  const renderUpcomingMeetings = () => {
    if (!guide.upcomingMeetings || guide.upcomingMeetings.length === 0) {
      return (
        <div className="text-center py-4">
          {guide.calendlyUrl && (
            <Button
              size={ButtonSize.SMALL}
              variant={ButtonVariant.PRIMARY}
              onClick={() => setActiveTab("schedule-meeting")}
            >
              {t("guide.schedule-meeting")}
            </Button>
          )}
        </div>
      );
    }
    return guide.upcomingMeetings.map((meeting) => {
      const { formattedDate, formattedTime } = formatMeetingDateTime(meeting);
      return (
        <div
          key={new Date(meeting).toISOString()}
          className="my-2 p-2 border rounded-md bg-white"
        >
          <div className="text-Grey-darker font-medium">
            <span>{formattedDate}</span>
            <span className="ml-4">{formattedTime}</span>
          </div>
        </div>
      );
    });
  };

  const renderPastMeetings = () => {
    if (!guide.pastMeetings || guide.pastMeetings.length === 0) {
      return null; // "Only show the 'Your Past Sessions' block if there is at least one past session."
    }
    return (
      <>
        {guide.pastMeetings.map((meeting) => {
          const { formattedDate, formattedTime } =
            formatMeetingDateTime(meeting);
          return (
            <div
              key={new Date(meeting).toISOString()}
              className="my-2 p-2 border rounded-md  bg-gray-50"
            >
              <div className="text-Grey-dark">
                <span>{formattedDate}</span>
                <span className="ml-4">{formattedTime}</span>
              </div>
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div>
      <div className="hidden lg:flex">
        <MyAreaTitle title={t("guide.title")} />
      </div>
      <div>
        <GuideHeader guideDetails={guide as GuideDetails} />
        <div className="flex w-full ">
          {tabs.map((tab) => (
            <div
              key={tab}
              className={`pt-6 pb-0 pl-0 pr-4 mt-6 cursor-pointer ${
                activeTab === tab ? "font-bold" : ""
              }`}
              onClick={() => setActiveTab(tab)}
            >
              <span className="p-2 pb-2 flex">{t(`guide.${tab}`)}</span>
              {activeTab === tab && (
                <div className="h-[2px] bg-Yellow-default w-full" />
              )}
            </div>
          ))}
        </div>
        <hr />
        {activeTab === "schedule-meeting" && (
          <div id="schedule-meeting" className="mt-4">
            {" "}
            {/**absolute -z-10 */}
            <div
              className={`calendly-inline-widget ${
                isMobile
                  ? "min-w-200px"
                  : "min-w-[1000px] lg:translate-x-[-35px]"
              }`}
              data-url={`${guide?.calendlyUrl}&text_color=202117&primary_color=fdc300`}
              data-resize="true"
              style={{
                height: "700px",
                padding: 0,
                margin: 0,
                border: "none",
              }}
            ></div>
          </div>
        )}
        {activeTab === "all-meetings" && (
          <div className="mt-4 space-y-6 p-2">
            <div>
              <h2 className="text-base font-semibold text-Grey-dark mb-3">
                {t("guide.upcoming-guide-meeting-title")}
              </h2>
              {renderUpcomingMeetings()}
            </div>

            {/* Past Meetings Section - Rendered only if there are past meetings */}
            {guide.pastMeetings && guide.pastMeetings.length > 0 && (
              <div>
                <h2 className="text-base font-semibold text-Grey-dark mb-3">
                  {t("guide.guide-meeting-history-title")}
                </h2>
                {renderPastMeetings()}
              </div>
            )}

            {(guide.upcomingMeetings.length > 0 ||
              guide.pastMeetings.length > 0) && (
              <div className="mt-6 text-Grey-dark text-xs ml-2">
                <InfoIcon
                  fontSize="small"
                  className="align-middle mr-1 text-Yellow-default"
                />
                {t("timezone-info")}
              </div>
            )}
          </div>
        )}
        <div id="about-me" className="mt-12 p-2">
          <div className="bg-Grey-light-3 rounded-md p-6">
            <h1 className="font-bold text-Grey-dark text-lg">
              {t(`guide.about-me`)}
            </h1>
            <div
              dangerouslySetInnerHTML={{ __html: guide?.aboutMe || "" }}
            ></div>
            <a href="#schedule-meeting">
              <div className="flex items-end justify-end lg:hidden">
                <Button
                  size={ButtonSize.SMALL}
                  variant={ButtonVariant.SECONDARY}
                  onClick={() => setActiveTab("schedule-meeting")}
                >
                  <div className="flex items-center ">
                    <span className="font-medium mr-3">
                      {t("guide.schedule-meeting")}
                    </span>
                    <div className="">
                      {" "}
                      <RightArrow />
                    </div>
                  </div>
                </Button>

                {/* <a href="#schedule-meeting">
                <Button
                  variant={ButtonVariant.SECONDARY}
                  size={ButtonSize.SMALL}
                  className="mr-5 px-3 mt-1"
                >
                  <div className="flex items-center ">
                    <span className="font-medium mr-3">
                      {t("guide.schedule-meeting")}
                    </span>
                    <div className="">
                      {" "}
                      <RightArrow />
                    </div>
                  </div>
                </Button>
              </a> */}
              </div>{" "}
            </a>
          </div>
        </div>
        {/* <div className="mt-4">
          <h1 className="font-bold text-Grey-dark text-lg mb-2">
            {t(`guide.meetings`)}
          </h1>
          <hr />

          <div className="flex w-full justify-end mt-4">
            <a href={guide?.calendlyUrl} target="_blank">
              <Button
                variant={ButtonVariant.SECONDARY}
                size={ButtonSize.SMALL}
                className="px-3 mt-1"
                onClick={() => null}
              >
                <span className="font-medium">
                  {t("guide.schedule-meeting")}
                </span>
              </Button>
            </a>
          </div>
        </div> */}
      </div>
    </div>
  );
}
